/* eslint-disable no-bitwise */
import _merge from 'lodash/merge';
import _get from 'lodash/get';
import { Lightning } from '@lightningjs/sdk';
import { SelectorHeader } from './selectorHeader/selectorHeader.component';
import { getTopBlockHeight, getInitialStyle, defaultContext, RIGHT_PADDING } from './selector.style';
import { getRendererRefByIndex, getSelectorTabs } from './selector.utils';
import { templates } from './selector.constants';
import { getBlockTemplateById } from '../../blocks';
import {
  IMPRESSION_TRACK_SIGNAL,
  IMPRESSION_UNTRACK_SIGNAL,
  PORTION_REQUESTED_SIGNAL,
  SELECTED_SIGNAL,
} from '../../../signals';
import { sendEvent } from '../../../../../logic/analytics/analytics.utils';
import { Row } from '../../../row/row.component';

export default class Selector extends Lightning.Component {
  static getOffsetsByBlockData(layoutData) {
    const { data } = layoutData;
    const blockType = getBlockTemplateById(data.blockTemplateId);

    const blockOffsets = blockType.getOffsetsByBlockData
      ? blockType.getOffsetsByBlockData({ ...layoutData, blockIndex: undefined })
      : { top: 0, bottom: 0 };

    return {
      ...blockOffsets,
      top: getTopBlockHeight(),
    };
  }

  static _template() {
    return _merge(
      {
        Tabs: {
          type: SelectorHeader,
          signals: {
            [SELECTED_SIGNAL]: '_handleSelectedTab',
          },
        },
        Content: {},
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
    this._contentPadding = 0;
    this._index = 0;
    this._selectedTabIndex = 0;
    this._rowHeight = 0;
    this._focusableTags = ['Tabs', getRendererRefByIndex(this._selectedTabIndex)];
    this._renderers = {};
  }

  _detach() {
    this._data = undefined;
    this._blocks = undefined;
    this._renderers = {};
  }

  get id() {
    return this._id;
  }

  get data() {
    return this._data;
  }

  set data(value) {
    if (!value) {
      return;
    }

    const { tabs, blocks } = getSelectorTabs(value);
    const header = this.tag('Tabs');
    header.w = value.rowSize.w;
    header.items = tabs;
    this._id = value.block.id;
    this._blockIndex = value.blockIndex;
    this._rowHeight = value.rowSize.h - getTopBlockHeight();
    this._data = value;
    this._blocks = blocks;
    this._handleSelectedTab(this._selectedTabIndex);
  }

  get contentPadding() {
    return this._contentPadding;
  }

  set contentPadding(value) {
    if (this._contentPadding !== value) {
      this._contentPadding = value;
      this.tag('Tabs').patch({
        x: value,
        leftPadding: value,
      });
      this.tag('Content').children.forEach(child => {
        child.patch({
          x: value,
          leftPadding: value,
        });
      });
    }
  }

  get focusMode() {
    return this._focusMode;
  }

  set focusMode(value) {
    if (value !== this._focusMode) {
      this.tag('Content').children.forEach(child => {
        child.focusMode = value;
      });
      this._focusMode = value;
    }
  }

  get focusedIndex() {
    const renderer = this._renderers[this._selectedTabIndex];
    if (!renderer) {
      return 0;
    }

    return (this._selectedTabIndex << 16) | (renderer.focusedIndex & 0xffff);
  }

  set focusedIndex(value) {
    const index = value & 0xffff;
    const tab = value >> 16;
    const renderer = this._getOrCreateRendererAtIndex(tab);
    const tabsView = this.tag('Tabs');

    if (!renderer) {
      return;
    }

    renderer.focusedIndex = index;
    tabsView.selectedIndex = tab;
    tabsView.goTo(tab);
    this._handleSelectedTab(tab);
    this._index = 1;
    this._refocus();

    const block = this._blocks[tab];
    if (!block || !block.content) {
      return;
    }

    const {
      pagination: { itemsPerPage },
    } = block.content;
    const page = Math.ceil(index / itemsPerPage) || 1;

    // Setting a focused index means a routing history restoration, hence we may need to refresh a progress bar, requesting current page
    this._handlePortionRequest({
      page,
      start: (page - 1) * itemsPerPage,
      id: block.id,
    });
  }

  get w() {
    return super.w;
  }

  set w(value) {
    super.w = value;
    this.tag('Content').children.forEach(child => {
      child.w = value;
    });
  }

  _getOrCreateRendererAtIndex(index) {
    if (this._renderers[index]) {
      return this._renderers[index];
    }

    const renderer = this.stage.c({
      type: Row,
      ref: getRendererRefByIndex(index),
      focusMode: this._focusMode,
      x: this._contentPadding,
      leftPadding: this._contentPadding,
      rightPadding: RIGHT_PADDING[this._style.screenSize],
      signals: {
        [PORTION_REQUESTED_SIGNAL]: '_handlePortionRequest',
        [SELECTED_SIGNAL]: '_handleConcurrentBlockSelect',
        [IMPRESSION_TRACK_SIGNAL]: '_handleImpressionTrack',
        [IMPRESSION_UNTRACK_SIGNAL]: '_handleImpressionUnTrack',
      },
    });
    this.tag('Content').childList.add(renderer);
    this._renderers[index] = renderer;

    return renderer;
  }

  _removeRendererAtIndex(index) {
    const renderer = this._renderers[index];
    if (!renderer) {
      return;
    }

    try {
      this.tag('Content').childList.remove(renderer);
    } finally {
      this._renderers[index] = null;
    }
  }

  _handleSelectedTab(tabIndex = this._selectedTabIndex) {
    const block = this._blocks[tabIndex];
    const renderer = this._getOrCreateRendererAtIndex(tabIndex);

    renderer.contentPadding = this._contentPadding;
    renderer.w = this.finalW;
    renderer.h = this._rowHeight;
    renderer.data = {
      ...this._data,
      block,
      blockIndex: undefined,
    };

    Object.values(this._renderers)
      .filter(Boolean)
      .forEach(view => {
        view.visible = view === renderer;
      });

    if (this._selectedTabIndex !== tabIndex) {
      sendEvent(_get(this._data, 'block.alternativeContent.analytics'));
    }

    this._focusableTags[1] = renderer.ref;

    if (this._selectedTabIndex !== tabIndex) {
      this._removeRendererAtIndex(this._selectedTabIndex);
    }

    this._selectedTabIndex = tabIndex;
  }

  _handleConcurrentBlockSelect(item) {
    sendEvent(_get(this._blocks, `[${this._selectedTabIndex}].analytics`));
    this.signal(SELECTED_SIGNAL, item);
  }

  _handlePortionRequest(portion) {
    this.signal(PORTION_REQUESTED_SIGNAL, {
      ...portion,
      parentId: this.id,
    });
  }

  _handleImpressionTrack(_, items) {
    this.signal(IMPRESSION_TRACK_SIGNAL, this._blockIndex, items);
  }

  _handleImpressionUnTrack(_, items) {
    this.signal(IMPRESSION_UNTRACK_SIGNAL, this._blockIndex, items);
  }

  _handleUp() {
    if (this._index > 0) {
      this._index -= 1;

      return true;
    }

    return false;
  }

  _handleDown() {
    if (this._index < this._focusableTags.length - 1) {
      this._index += 1;

      return true;
    }

    return false;
  }

  _getFocused() {
    return this.tag(this._focusableTags[this._index]);
  }
}

Selector.Templates = templates;
